import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MarkdownEditor from './MarkdownEditor';
import { BlogPost } from '../types';
import { fetchDocumentByID, updateBio } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const BioEditor: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Extract ID from URL path, e.g. /editor/{uuid}
  const id = location.pathname.split('/').pop() || '';
  const [post, setPost] = useState<BlogPost>({
    id: crypto.randomUUID(),
    content: '',
    subtitle: '',
    author_username: 'current-user',
    posted_at: '',
  });
  const [title, setTitle] = useState('');
  // Removed showPublishOptions state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing post if slug is provided
  useEffect(() => {
    const loadPost = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const document = await fetchDocumentByID(id);

        if (document) {
          setPost(document);
          setTitle(document.title || '');
        } else {
          setPost({
            id: id,
            content: '',
            subtitle: '',
            author_username: '',
            posted_at: '',
          });
          setTitle('Bio');
        }
      } catch (err) {
        console.error('Failed to load blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  const handleContentChange = (content: string) => {
    setPost(prev => ({ ...prev, content }));
  };

  const handlePublish = async () => {
    if (!user) {
      setError('You must be logged in to publish');
      return;
    }
    try {
      await updateBio(user?.username, post.content, 'published');
      navigate('/profile');
    } catch (err) {
      setError('Failed to publish document');
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="blog-post-editor">
        <div className="loading-state">Loading post...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-editor">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/profile')}>Back to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-editor">
      <main className="editor-main">
        <MarkdownEditor
          onContentChange={handleContentChange}
          placeholder="Tell your story..."
          initialContent={post.content}
          key={post.id} // Force re-render when editing different posts
        />
      </main>

      <footer className="editor-footer">
        <div className="footer-left">
          <span className="word-count">
            {post.content.length} characters
          </span>
        </div>
        <div className="footer-right">
          <button
            className="btn btn-tertiary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handlePublish}
          >
            Publish
          </button>
        </div>
      </footer>
    </div>
  );
};

export default BioEditor;